import { hasConsent } from "@/lib/cookieConsent";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

/** Derive a coarse page_type from the URL path. */
function derivePageType(pathname: string): string {
  if (pathname === "/" || pathname === "") return "home";
  if (pathname.startsWith("/guides")) return "guide";
  if (pathname.startsWith("/repair/")) {
    // /repair/:brand/:model/:city → model_city, /repair/:brand/:model → model
    const parts = pathname.split("/").filter(Boolean);
    if (parts.length >= 4) return "model_city";
    if (parts.length === 3) return "model";
    return "service";
  }
  if (pathname.startsWith("/repairs")) return "service";
  if (pathname.startsWith("/city/")) return "city";
  if (pathname.startsWith("/neighborhood/")) return "neighborhood";
  if (pathname.startsWith("/model/")) return "model";
  if (pathname.startsWith("/about")) return "about";
  if (pathname.startsWith("/contact")) return "contact";
  if (pathname.startsWith("/faq")) return "faq";
  return "other";
}

/** Read a data-* attribute by walking up from the anchor. Returns undefined when absent. */
function readDataAttr(el: HTMLElement | null, name: string): string | undefined {
  if (!el) return undefined;
  const node = el.closest(`[${name}]`) as HTMLElement | null;
  const v = node?.getAttribute(name);
  return v && v.trim() ? v.trim() : undefined;
}

/**
 * Global click tracker for Meta Pixel + GTM dataLayer.
 * Fires fbq('track', 'Contact', {...}) on WhatsApp / phone clicks
 * with rich context (page_type, cta_location, device_model, issue, city)
 * pulled from data-* attributes on the clicked CTA.
 *
 * Safe-by-default: never throws when fbq is missing.
 */
const MetaPixelTracker = () => {
  const location = useLocation();

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      // Allow modifier-key clicks to behave natively
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;

      const target = e.target as HTMLElement | null;
      if (!target) return;
      const anchor = target.closest("a") as HTMLAnchorElement | null;
      if (!anchor) return;
      const href = anchor.getAttribute("href") || "";
      if (!href) return;

      let channel: "whatsapp" | "call" | null = null;
      if (href.includes("wa.me") || href.startsWith("whatsapp:")) channel = "whatsapp";
      else if (href.startsWith("tel:")) channel = "call";
      if (!channel) return;

      const page = location.pathname + location.search;
      const eventId = `${channel}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

      // Pull rich context from data attributes (closest ancestor that has them)
      const ctaType = readDataAttr(anchor, "data-cta-type") || (channel === "whatsapp" ? "whatsapp" : "call");
      const ctaLocation = readDataAttr(anchor, "data-cta-location") || "unknown";
      const pageType = readDataAttr(anchor, "data-page-type") || derivePageType(location.pathname);
      const deviceModel = readDataAttr(anchor, "data-model");
      const issue = readDataAttr(anchor, "data-issue");
      const city = readDataAttr(anchor, "data-city");

      const eventData: Record<string, unknown> = {
        content_name: channel === "whatsapp" ? "WhatsApp Click" : "Call Click",
        content_category: channel,
        source_page: page,
        page_title: document.title,
        page_type: pageType,
        cta_type: ctaType,
        cta_location: ctaLocation,
      };
      if (deviceModel) eventData.device_model = deviceModel;
      if (issue) eventData.issue = issue;
      if (city) eventData.city = city;

      // Google Analytics 4 (gtag)
      if (typeof window !== "undefined" && hasConsent("analytics") && typeof window.gtag === "function") {
        try {
          const gaEvent = channel === "whatsapp" ? "whatsapp_click" : "call_us_click";
          const gaParams: Record<string, unknown> = {
            event_category: "engagement",
            event_label: ctaLocation,
            cta_location: ctaLocation,
            cta_type: ctaType,
            page_type: pageType,
            page_path: page,
          };
          if (deviceModel) gaParams.device_model = deviceModel;
          if (issue) gaParams.issue = issue;
          if (city) gaParams.city = city;
          window.gtag("event", gaEvent, gaParams);
        } catch { /* no-op */ }
      }

      // Meta Pixel
      if (typeof window !== "undefined" && hasConsent("marketing") && typeof window.fbq === "function") {
        try {
          window.fbq("track", "Contact", eventData, { eventID: eventId });
          window.fbq(
            "trackCustom",
            channel === "whatsapp" ? "WhatsAppContact" : "CallContact",
            eventData,
            { eventID: eventId },
          );
        } catch {
          /* no-op */
        }
      }

      // GTM dataLayer — single push per click (no duplicate of fbq events)
      try {
        if (hasConsent("analytics") && hasConsent("marketing")) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: channel === "whatsapp" ? "whatsapp_click" : "call_click",
          event_id: eventId,
          ...eventData,
        });
        }
      } catch {
        /* no-op */
      }

      // For tel: links: in-app browsers (Instagram, Facebook, TikTok) often
      // block tel: navigation entirely. Detect and fall back to copying the
      // number + opening WhatsApp so the user can still reach us.
      if (channel === "call") {
        const ua = navigator.userAgent || "";
        const inApp = /Instagram|FBAN|FBAV|FB_IAB|FBIOS|Messenger|Line\/|MicroMessenger|TikTok|musical_ly/i.test(ua);
        if (inApp) {
          e.preventDefault();
          const phone = href.replace(/^tel:/, "").replace(/[^\d+]/g, "");
          // Try clipboard copy (best-effort)
          try {
            navigator.clipboard?.writeText(phone);
          } catch { /* no-op */ }
          // Try tel: anyway via location (works on some Android in-app)
          try { window.location.href = href; } catch { /* no-op */ }
          // Fallback: open WhatsApp so the user has a working contact path
          setTimeout(() => {
            const waMsg = encodeURIComponent("היי, ניסיתי להתקשר מאינסטגרם וזה לא עבד. אשמח לחזרה.");
            window.location.href = `https://wa.me/972585449992?text=${waMsg}`;
          }, 800);
          return;
        }
        // Regular browser: let native tel: behavior run.
        return;
      }


      // WhatsApp: small delay so the pixel can flush before redirecting.
      const targetAttr = anchor.getAttribute("target");
      e.preventDefault();
      setTimeout(() => {
        if (targetAttr === "_blank") {
          window.open(href, "_blank", "noopener,noreferrer");
        } else {
          window.location.href = href;
        }
      }, 300);
    };

    document.addEventListener("click", handler, { capture: true });
    return () => document.removeEventListener("click", handler, { capture: true } as EventListenerOptions);
  }, [location.pathname, location.search]);

  return null;
};

export default MetaPixelTracker;
